import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import ReactQuill from "react-quill";

/**
 * Floating editor for Quill image embeds (width, height, alt, block align, remove).
 * Opens at pointer position so it stays near the image / cursor.
 */
export function WorkJournalImagePopover({ quill, imgEl, open, anchorPosition, onClose, onApplied }) {
  const { t } = useTranslation();
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [alt, setAlt] = useState("");

  const getBlot = useCallback(() => {
    if (!quill || !imgEl) return null;
    const Q = ReactQuill.Quill;
    if (!Q) return null;
    try {
      return Q.find(imgEl);
    } catch {
      return null;
    }
  }, [quill, imgEl]);

  useEffect(() => {
    if (!imgEl || !open) return;
    setWidth(imgEl.getAttribute("width") || "");
    setHeight(imgEl.getAttribute("height") || "");
    setAlt(imgEl.getAttribute("alt") || "");
  }, [imgEl, open]);

  const applyToBlot = useCallback(
    (fn) => {
      const blot = getBlot();
      if (!blot || !quill) return;
      fn(blot);
      onApplied?.();
    },
    [getBlot, quill, onApplied]
  );

  const handleApplyFields = () => {
    applyToBlot((blot) => {
      blot.format("width", width.trim() || false);
      blot.format("height", height.trim() || false);
      blot.format("alt", alt.trim() || false);
    });
  };

  const applyPresetWidth = (value) => {
    setWidth(value);
    setHeight("");
    applyToBlot((blot) => {
      blot.format("width", value || false);
      blot.format("height", false);
    });
  };

  const applyAlign = (align) => {
    const blot = getBlot();
    if (!blot || !quill) return;
    const index = quill.getIndex(blot);
    if (align === false || align === "" || align == null) {
      quill.formatLine(index, 1, "align", false, "user");
    } else {
      quill.formatLine(index, 1, "align", align, "user");
    }
    onApplied?.();
  };

  const removeImage = () => {
    const blot = getBlot();
    if (!blot || !quill) return;
    const index = quill.getIndex(blot);
    quill.deleteText(index, 1, "user");
    onClose();
  };

  if (!anchorPosition) return null;

  return (
    <Popover
      open={open && Boolean(anchorPosition)}
      anchorReference="anchorPosition"
      anchorPosition={{ top: anchorPosition.top, left: anchorPosition.left }}
      onClose={onClose}
      onMouseDown={(e) => e.preventDefault()}
      slotProps={{
        paper: {
          sx: { p: 2, maxWidth: 320, zIndex: 2000 },
        },
      }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Typography variant="subtitle2" className="mb-2 font-semibold text-gray-800">
        {t("todo.journalImageTitle")}
      </Typography>
      <Stack spacing={1.5}>
        <TextField
          size="small"
          label={t("todo.journalImageWidth")}
          value={width}
          onChange={(e) => setWidth(e.target.value)}
          placeholder="320, 50%, 100%"
          fullWidth
          helperText={t("todo.journalImageWidthHint")}
        />
        <TextField
          size="small"
          label={t("todo.journalImageHeight")}
          value={height}
          onChange={(e) => setHeight(e.target.value)}
          placeholder={t("todo.journalImageHeightPlaceholder")}
          fullWidth
        />
        <TextField
          size="small"
          label={t("todo.journalImageAlt")}
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          fullWidth
        />
        <div>
          <Typography variant="caption" className="text-gray-600 block mb-1">
            {t("todo.journalImageSizePresets")}
          </Typography>
          <ButtonGroup size="small" variant="outlined" sx={{ flexWrap: "wrap" }}>
            <Button onClick={() => applyPresetWidth("33%")}>33%</Button>
            <Button onClick={() => applyPresetWidth("50%")}>50%</Button>
            <Button onClick={() => applyPresetWidth("75%")}>75%</Button>
            <Button onClick={() => applyPresetWidth("100%")}>100%</Button>
            <Button onClick={() => applyPresetWidth("")}>{t("todo.journalImageOriginal")}</Button>
          </ButtonGroup>
        </div>
        <div>
          <Typography variant="caption" className="text-gray-600 block mb-1">
            {t("todo.journalImageAlign")}
          </Typography>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => applyAlign(false)}>{t("todo.journalImageAlignLeft")}</Button>
            <Button onClick={() => applyAlign("center")}>{t("todo.journalImageAlignCenter")}</Button>
            <Button onClick={() => applyAlign("right")}>{t("todo.journalImageAlignRight")}</Button>
          </ButtonGroup>
        </div>
        <Stack direction="row" spacing={1} flexWrap="wrap" className="pt-1">
          <Button size="small" variant="contained" onClick={handleApplyFields}>
            {t("todo.journalImageApply")}
          </Button>
          <Button size="small" color="error" variant="outlined" onClick={removeImage}>
            {t("todo.journalImageRemove")}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
